'use client';

import { useState } from 'react';

import { validateIdentityNumberFormat } from '@/lib/identityNumber';
import { phoneNumberSchema } from '@/lib/phone-number-schema';
import { validateSerialNumberFormat } from '@/lib/serialNumber';
import {
  CalendarDays,
  CreditCard,
  MapPin,
  Send,
  Truck,
  UserRound,
} from '@holistic/components/icons';

import type { CargoLocation } from '@/types/cargo';
import type { AddressErrors } from './cargo/address-fields';
import type { GuestCheckoutErrors } from './cargo/guest-checkout';
import type { PaymentFormValues } from './cargo/payment-schema';
import type { CargoFlow } from './cargo/use-cargo-flow';
import type { StepParam } from './constants';
import type { SummaryRow, TalepRequestType } from './types';

import AddressFields from './cargo/address-fields';
import CheckoutSection from './cargo/checkout-section';
import GuestCheckout from './cargo/guest-checkout';
import InfoBanner from './cargo/info-banner';
import PaymentCard from './cargo/payment-card';
import PickupDateField from './cargo/pickup-date-field';
import ShippingMethodSelector from './cargo/shipping-method-selector';
import { usePickupDates } from './cargo/use-pickup-dates';
import DeviceInfoCard from './device-info-card';
import FlowCard from './flow-card';
import SummaryCard from './summary-card';

const ADDRESS_LINE_MIN = 10;

type CheckoutErrors = AddressErrors &
  GuestCheckoutErrors & { pickupDate?: string };

type ConfirmStepProps = {
  summary: SummaryRow[];
  /** Seçilen arızalar (fiyatlarıyla); ödeme özetindeki döküm buradan çıkar. */
  requestTypes: TalepRequestType[];
  onEdit: (key: StepParam) => void;
  description: string;
  onDescriptionChange: (v: string) => void;
  serialNumber: string;
  onSerialNumberChange: (v: string) => void;
  serialNumberError: string | null;
  onSerialNumberError: (error: string | null) => void;
  identityNumber: string;
  onIdentityNumberChange: (v: string) => void;
  identityNumberError: string | null;
  onIdentityNumberError: (error: string | null) => void;
  warrantyId: string | null;
  onWarrantySelect: (warrantyId: string | null, serialNumber?: string) => void;
  cargo: CargoFlow;
  /** Kart bilgileriyle çağrılırsa 3D Secure ödemesi başlar; `null` ise tahsilat yok. */
  onSubmit: (card: PaymentFormValues | null) => void;
  isSubmitting: boolean;
  /** Giriş yapmışsa misafir seçeneği hiç gösterilmez. */
  isLoggedIn: boolean;
  isGuestMode: boolean;
  onChooseGuest: () => void;
  onChooseLogin: () => void;
  onChooseRegister: () => void;
  guestFirstName: string;
  onGuestFirstNameChange: (v: string) => void;
  guestLastName: string;
  onGuestLastNameChange: (v: string) => void;
  guestPhoneNumber: string;
  onGuestPhoneNumberChange: (v: string) => void;
  guestEmail: string;
  onGuestEmailChange: (v: string) => void;
};

/**
 * Son adım: cihaz bilgileri, kargo adresi, randevu günü ve ödeme tek ekranda.
 *
 * Bu ekranda backend'e hiçbir ara kayıt yazılmaz; her şey "Öde ve Gönder"
 * anındaki tek POST'a kadar bellekte durur, kullanıcı serbestçe geri dönebilir.
 * O POST, tahsilat varsa `payment/initiate` (3D Secure), yoksa
 * `repair-request/add` olur.
 */
const ConfirmStep = ({
  summary,
  requestTypes,
  onEdit,
  description,
  onDescriptionChange,
  serialNumber,
  onSerialNumberChange,
  serialNumberError,
  onSerialNumberError,
  identityNumber,
  onIdentityNumberChange,
  identityNumberError,
  onIdentityNumberError,
  warrantyId,
  onWarrantySelect,
  cargo,
  onSubmit,
  isSubmitting,
  isLoggedIn,
  isGuestMode,
  onChooseGuest,
  onChooseLogin,
  onChooseRegister,
  guestFirstName,
  onGuestFirstNameChange,
  guestLastName,
  onGuestLastNameChange,
  guestPhoneNumber,
  onGuestPhoneNumberChange,
  guestEmail,
  onGuestEmailChange,
}: ConfirmStepProps) => {
  const [errors, setErrors] = useState<CheckoutErrors>({});
  const { address, pickupDate } = cargo;

  // Takvim il+ilçeye bağlı; ikisi de seçilene kadar istek atılmaz (`skipToken`).
  const dates = usePickupDates(address.city, address.town);

  const clearError = (key: keyof CheckoutErrors) =>
    setErrors((prev) => ({ ...prev, [key]: undefined }));

  const selectPickupDate = (date: string | null) => {
    clearError('pickupDate');
    cargo.setPickupDate(date);
  };

  // Cascade'de üst seviye değişince randevu günü de düştüğü için ilgili hata
  // mesajları da temizlenir.
  const selectCity = (city: CargoLocation | null) => {
    clearError('city');
    clearError('pickupDate');
    cargo.setCity(city);
  };

  const selectTown = (town: CargoLocation | null) => {
    clearError('town');
    clearError('pickupDate');
    cargo.setTown(town);
  };

  const isTv = summary.some(
    (s) =>
      s.key === 'category' &&
      (s.value.toLowerCase().includes('tv') ||
        s.value.toLowerCase().includes('televizyon'))
  );

  // Bölümler kademeli açılır: randevu günleri ilçeye, ödeme de teslimat
  // bilgilerine bağlı olduğu için önceki bölüm tamamlanmadan gösterilmez.
  const isAddressComplete =
    !!address.city &&
    !!address.town &&
    address.addressLine.trim().length >= ADDRESS_LINE_MIN;

  const isIdentityReady = isLoggedIn || isGuestMode;

  const isPickupComplete = isTv
    ? isAddressComplete && isIdentityReady
    : isAddressComplete && !!pickupDate && isIdentityReady;

  /** Gönderim öncesi tüm ekranın doğrulaması; hatalar ilgili karta yazılır. */
  const validate = () => {
    const serialError = validateSerialNumberFormat(serialNumber);
    onSerialNumberError(serialError);

    const idError = validateIdentityNumberFormat(identityNumber);
    onIdentityNumberError(idError);

    const next: CheckoutErrors = {};
    if (!address.city) next.city = 'İl seçin.';
    if (!address.town) next.town = 'İlçe seçin.';
    if (address.addressLine.trim().length < ADDRESS_LINE_MIN) {
      next.addressLine =
        'Kuryenin adresi bulabilmesi için mahalle, sokak ve kapı numarasını yazın.';
    }
    if (!isTv && !pickupDate && address.city && address.town) {
      next.pickupDate = dates.hasDays
        ? 'Kuryenin geleceği günü seçin.'
        : 'Bu ilçe için randevu günü yok; teslim alma adresini değiştirin.';
    }
    if (
      isTv &&
      address.city &&
      !address.city.name.toLowerCase().includes('istanbul') &&
      !address.city.name.toLowerCase().includes('i̇stanbul')
    ) {
      next.city = 'Özel saha teslimatı yalnızca İstanbul ili için geçerlidir.';
    }

    if (!isLoggedIn && isGuestMode) {
      if (!guestFirstName.trim()) next.firstName = 'Ad zorunludur.';
      if (!guestLastName.trim()) next.lastName = 'Soyad zorunludur.';
      if (!guestEmail.trim()) {
        next.email = 'E-posta zorunludur.';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail.trim())) {
        next.email = 'Geçerli bir e-posta adresi giriniz.';
      }
      if (!guestPhoneNumber.trim()) {
        next.phoneNumber = 'Telefon numarası zorunludur.';
      } else {
        const phoneResult = phoneNumberSchema.safeParse(guestPhoneNumber);
        if (!phoneResult.success) {
          next.phoneNumber =
            phoneResult.error.issues[0]?.message ??
            'Geçerli bir cep telefonu giriniz.';
        }
      }
    }

    setErrors(next);
    return !serialError && !idError && Object.keys(next).length === 0;
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Onay adımında görünür bir sayfa başlığı yok; sayfa yine tek H1 taşısın
          diye ekran okuyucu/arama motoru için gizli başlık. */}
      <h1 className="sr-only">Talebinizi onaylayın</h1>

      <div className="md:hidden">
        <SummaryCard
          summary={summary}
          requestTypes={requestTypes}
          isWarrantyCovered={!!warrantyId}
          onEdit={onEdit}
        />
      </div>

      <DeviceInfoCard
        description={description}
        onDescriptionChange={onDescriptionChange}
        serialNumber={serialNumber}
        onSerialNumberChange={onSerialNumberChange}
        serialNumberError={serialNumberError}
        identityNumber={identityNumber}
        onIdentityNumberChange={onIdentityNumberChange}
        identityNumberError={identityNumberError}
        warrantyId={warrantyId}
        onWarrantySelect={onWarrantySelect}
        isLoggedIn={isLoggedIn}
      />

      {isTv ? (
        <InfoBanner
          icon={Truck}
          title="Televizyonlar Özel Saha Ekibimizle Teslim Alınır (Yalnızca İstanbul)"
        >
          Televizyon gibi büyük ve hassas cihazlar standart kargo ile
          taşınmamaktadır. Cihazınızı uzman saha ekibimiz yalnızca{' '}
          <strong>İstanbul içi</strong> adresinizden özel korumalı ambalaj ile
          gelip teslim alacaktır. Talebinizi oluşturduktan sonra ekiplerimiz
          randevu planlamak için sizinle iletişime geçecektir.
        </InfoBanner>
      ) : (
        <>
          <FlowCard
            icon={Send}
            title="Gönderim Yöntemi"
            bodyClassName="flex flex-col gap-4 p-5"
          >
            <ShippingMethodSelector
              value={cargo.shippingMethod}
              onChange={cargo.setShippingMethod}
            />
          </FlowCard>

          <InfoBanner icon={Truck} title="Kurye adresinizden alacak">
            Cihazınız bu adresten ücretsiz teslim alınır, tamir bitince yine
            aynı adrese gönderilir.
          </InfoBanner>
        </>
      )}

      <FlowCard
        icon={MapPin}
        title="Teslim Alma Adresi"
        bodyClassName="flex flex-col gap-5 p-5"
      >
        <AddressFields
          address={address}
          errors={errors}
          isOnlyIstanbul={isTv}
          onCityChange={selectCity}
          onTownChange={selectTown}
          onDistrictChange={cargo.setDistrict}
          onAddressLineChange={cargo.setAddressLine}
          onClearError={clearError}
        />
      </FlowCard>

      {isTv ? (
        <FlowCard
          icon={CalendarDays}
          title="Randevu Planlaması"
          bodyClassName="flex flex-col gap-2 p-5 text-left"
        >
          <div className="text-primary flex items-center gap-2 text-sm font-semibold">
            <CalendarDays className="text-primary size-4" />
            <span>Saha Ekibimiz Sizi Arayacak</span>
          </div>
          <p className="text-xs leading-relaxed text-gray-500">
            Talebiniz bize ulaştıktan sonra uzman operasyon ekibimiz kayıtlı
            telefon numaranızdan sizinle iletişime geçerek en uygun gün ve
            saatte adresten teslim alma randevusunu planlayacaktır.
          </p>
        </FlowCard>
      ) : (
        <CheckoutSection
          icon={CalendarDays}
          title="Randevu Günü"
          lockedText="Kuryenin geleceği günleri görmek için önce teslim alma adresini tamamlayın."
          isLocked={!isAddressComplete}
          bodyClassName="p-5"
        >
          <PickupDateField
            dates={dates}
            value={pickupDate}
            error={errors.pickupDate}
            onSelect={selectPickupDate}
          />
        </CheckoutSection>
      )}

      {!isLoggedIn && (
        <FlowCard
          icon={UserRound}
          title="Hesap"
          bodyClassName="flex flex-col gap-4 p-5"
        >
          <GuestCheckout
            isGuestMode={isGuestMode}
            onChooseGuest={onChooseGuest}
            onChooseLogin={onChooseLogin}
            onChooseRegister={onChooseRegister}
            firstName={guestFirstName}
            onFirstNameChange={onGuestFirstNameChange}
            lastName={guestLastName}
            onLastNameChange={onGuestLastNameChange}
            phoneNumber={guestPhoneNumber}
            onPhoneNumberChange={onGuestPhoneNumberChange}
            email={guestEmail}
            onEmailChange={onGuestEmailChange}
            identityNumber={identityNumber}
            onIdentityNumberChange={onIdentityNumberChange}
            identityNumberError={identityNumberError}
            errors={{
              firstName: errors.firstName,
              lastName: errors.lastName,
              phoneNumber: errors.phoneNumber,
              email: errors.email,
            }}
          />
        </FlowCard>
      )}

      <CheckoutSection
        icon={CreditCard}
        title="Ön Teklif & Onay"
        lockedText={
          (isTv ? isAddressComplete : isAddressComplete && !!pickupDate) &&
          !isIdentityReady
            ? 'Devam etmek için giriş yapın, üye olun ya da üye olmadan devam edin.'
            : isTv
              ? 'Teslim alma adresini girdiğinizde onay adımı açılır.'
              : 'Adres ve randevu gününü seçtiğinizde onay adımı açılır.'
        }
        isLocked={!isPickupComplete}
        bodyClassName="p-5"
      >
        <PaymentCard
          address={address}
          pickupDate={isTv ? null : pickupDate}
          requestTypes={requestTypes}
          isWarrantyCovered={!!warrantyId}
          validate={validate}
          onSubmit={onSubmit}
          isSubmitting={isSubmitting}
        />
      </CheckoutSection>
    </div>
  );
};

export default ConfirmStep;
